"use client";

import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNotifications, type NotificationType } from "@/lib/notifications/context";

// ── Icons (inline SVGs — no dep needed) ──────────────────

function CheckCircleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="shrink-0">
      <path
        d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.707-9.293a1 1 0 0 0-1.414-1.414L9 10.586 7.707 9.293a1 1 0 0 0-1.414 1.414l2 2a1 1 0 0 0 1.414 0l4-4Z"
        fill="currentColor"
      />
    </svg>
  );
}

function XCircleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="shrink-0">
      <path
        d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM8.28 7.22a.75.75 0 0 0-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 1 0 1.06 1.06L10 11.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L11.06 10l1.72-1.72a.75.75 0 0 0-1.06-1.06L10 8.94 8.28 7.22Z"
        fill="currentColor"
      />
    </svg>
  );
}

function AlertTriangleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="shrink-0">
      <path
        d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.168 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 7a.75.75 0 0 1 .75.75v3a.75.75 0 0 1-1.5 0v-3A.75.75 0 0 1 10 7Zm1 6.25a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z"
        fill="currentColor"
      />
    </svg>
  );
}

function InfoCircleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="shrink-0">
      <path
        d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm.75-11.25a.75.75 0 0 0-1.5 0v.5a.75.75 0 0 0 1.5 0v-.5ZM10 9a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 9Z"
        fill="currentColor"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M4.28 3.22a.75.75 0 0 0-1.06 1.06L6.94 8l-3.72 3.72a.75.75 0 1 0 1.06 1.06L8 9.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L9.06 8l3.72-3.72a.75.75 0 0 0-1.06-1.06L8 6.94 4.28 3.22Z"
        fill="currentColor"
      />
    </svg>
  );
}

// ── Config ───────────────────────────────────────────────

const TYPE_CONFIG: Record<
  NotificationType,
  { icon: React.FC; accentClass: string; iconClass: string; progressClass: string }
> = {
  success: {
    icon: CheckCircleIcon,
    accentClass: "bg-success",
    iconClass: "text-success",
    progressClass: "bg-success/30",
  },
  error: {
    icon: XCircleIcon,
    accentClass: "bg-error",
    iconClass: "text-error",
    progressClass: "bg-error/30",
  },
  warning: {
    icon: AlertTriangleIcon,
    accentClass: "bg-warning",
    iconClass: "text-warning",
    progressClass: "bg-warning/30",
  },
  info: {
    icon: InfoCircleIcon,
    accentClass: "bg-accent",
    iconClass: "text-accent",
    progressClass: "bg-accent/30",
  },
};

// ── Single notification card ─────────────────────────────

interface NotificationCardProps {
  id: string;
  type: NotificationType;
  title: string;
  message?: string | undefined;
  duration?: number | undefined;
  onDismiss: (id: string) => void;
}

function NotificationCard({
  id,
  type,
  title,
  message,
  duration,
  onDismiss,
}: NotificationCardProps) {
  const config = TYPE_CONFIG[type];
  const Icon = config.icon;

  return (
    <div
      role="alert"
      className="notif-enter pointer-events-auto relative flex w-80 overflow-hidden rounded-lg border border-border bg-surface shadow-lg"
    >
      {/* Left accent bar */}
      <div className={`w-1 shrink-0 ${config.accentClass}`} />

      {/* Content */}
      <div className="flex min-w-0 flex-1 items-start gap-3 px-4 py-3">
        <div className={`mt-0.5 ${config.iconClass}`}>
          <Icon />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-text-primary">{title}</p>
          {message && (
            <p className="mt-0.5 text-sm text-text-secondary">{message}</p>
          )}
        </div>
        <button
          onClick={() => onDismiss(id)}
          className="mt-0.5 shrink-0 rounded p-0.5 text-text-tertiary transition-colors hover:text-text-primary"
          aria-label="Dismiss"
        >
          <CloseIcon />
        </button>
      </div>

      {/* Progress bar for auto-dismiss */}
      {duration && duration > 0 && (
        <div
          className={`absolute bottom-0 left-1 right-0 h-0.5 ${config.progressClass}`}
        >
          <div
            className={`h-full ${config.accentClass} opacity-60`}
            style={{
              animation: `notif-progress ${duration}ms linear forwards`,
            }}
          />
        </div>
      )}
    </div>
  );
}

// ── Notification stack (portal-rendered) ─────────────────

export function NotificationRenderer() {
  const { notifications, dismiss } = useNotifications();
  const containerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // Create or find portal container
    let el = document.getElementById("notification-portal");
    if (!el) {
      el = document.createElement("div");
      el.id = "notification-portal";
      el.className =
        "fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none";
      document.body.appendChild(el);
    }
    containerRef.current = el;

    return () => {
      // Clean up only if empty
      if (el && el.childNodes.length === 0 && el.parentNode) {
        el.parentNode.removeChild(el);
      }
    };
  }, []);

  if (!containerRef.current || notifications.length === 0) return null;

  return createPortal(
    <>
      {notifications.map((n) => (
        <NotificationCard
          key={n.id}
          id={n.id}
          type={n.type}
          title={n.title}
          message={n.message}
          duration={n.duration}
          onDismiss={dismiss}
        />
      ))}
    </>,
    containerRef.current,
  );
}
